// Decode Google's signed delta format, rejecting incomplete or oversized values.
export const decodePolyline = (encoded = '') => {
  let index = 0; let latitude = 0; let longitude = 0;
  const points = [];
  const read = () => {
    let value = 0; let shift = 0; let byte;
    do {
      if (index >= encoded.length || shift > 30) throw new Error('Invalid planned route geometry.');
      byte = encoded.charCodeAt(index) - 63; index += 1;
      if (byte < 0 || byte > 63) throw new Error('Invalid planned route geometry.');
      value += (byte % 32) * (2 ** shift); shift += 5;
    } while (byte >= 32);
    return value % 2 ? -(Math.floor(value / 2) + 1) : value / 2;
  };
  while (index < encoded.length) {
    latitude += read(); longitude += read();
    if (Math.abs(latitude) > 9000000 || Math.abs(longitude) > 18000000) throw new Error('Invalid planned route geometry.');
    points.push({ latitude: latitude / 1e5, longitude: longitude / 1e5 });
  }
  return points;
};

const valid = (p) => p && p.valid !== false && p.latitude != null && p.longitude != null
  && Number.isFinite(Number(p.latitude)) && Number.isFinite(Number(p.longitude))
  && Math.abs(p.latitude) <= 90 && Math.abs(p.longitude) <= 180;
const xy = (p, origin) => [
  (p.longitude - origin.longitude) * 111195 * Math.cos((origin.latitude * Math.PI) / 180),
  (p.latitude - origin.latitude) * 111195,
];

// Project onto segments, not just vertices: a truck halfway along a long straight segment
// must still count as on-route. Progress is the latest position along the planned line.
export const compareRoute = (plan, samples, { tolerance = 500, now = Date.now(), maxAgeMs = 300000 } = {}) => {
  if (plan.length < 2 || !plan.every(valid)) return null;
  const fixes = samples.filter((p) => valid(p) && Number.isFinite(Date.parse(p.fixTime)) && Date.parse(p.fixTime) <= now
    && !(p.accuracy > tolerance)).sort((a, b) => Date.parse(a.fixTime) - Date.parse(b.fixTime))
    .filter((p, i, all) => !i || p.fixTime !== all[i - 1].fixTime);
  if (!fixes.length) return null;
  let total = 0;
  const segments = plan.slice(1).map((end, i) => {
    const start = plan[i]; const [dx, dy] = xy(end, start); const length = Math.hypot(dx, dy);
    const segment = { start, dx, dy, length, offset: total }; total += length; return segment;
  });
  if (!total) return null;
  const projected = fixes.map((p) => {
    let best = { distance: Infinity, along: 0 };
    segments.forEach(({ start, dx, dy, length, offset }) => {
      const [x, y] = xy(p, start);
      const fraction = length ? Math.max(0, Math.min(1, (x * dx + y * dy) / (length * length))) : 0;
      const distance = Math.hypot(x - fraction * dx, y - fraction * dy);
      if (distance < best.distance) best = { distance, along: offset + fraction * length };
    });
    return best;
  });
  const latest = projected[projected.length - 1];
  const stale = now - Date.parse(fixes[fixes.length - 1].fixTime) > maxAgeMs;
  const sustained = projected.length >= 3 && projected.slice(-3).every((p) => p.distance > tolerance)
    && Date.parse(fixes[fixes.length - 1].fixTime) - Date.parse(fixes[fixes.length - 3].fixTime) <= maxAgeMs;
  return {
    percent: (latest.along / total) * 100,
    remainingFraction: 1 - latest.along / total,
    plannedMeters: total,
    deviationMeters: latest.distance,
    stale,
    status: stale ? 'GPS stale' : sustained ? 'Off route' : latest.distance > tolerance ? 'Possible deviation' : 'On route',
    actualMeters: fixes.slice(1).reduce((sum, p, i) => sum + Math.hypot(...xy(p, fixes[i])), 0),
    maxDeviationMeters: projected.reduce((worst, p) => Math.max(worst, p.distance), 0),
  };
};
